export type SoundSection = { startMs: number; endMs: number };

const WINDOW_MS = 20;
// Pauses shorter than this keep the music ducked to avoid pumping between phrases.
const MIN_GAP_MS = 2500;
const MIN_SECTION_MS = 120;
// Relative to the loud parts, so quiet recordings and noisy uploads both work.
const RELATIVE_THRESHOLD_DB = 30;
const ABSOLUTE_FLOOR_DB = -50;

const DUCKED_VOLUME = 0.15;
const LOOKAHEAD_MS = 0;
const ATTACK_MS = 600;
const RELEASE_DELAY_MS = 100;
const RELEASE_MS = 1000;

export function detectSoundSections(
  channels: Float32Array[],
  sampleRate: number,
): SoundSection[] {
  const length = channels[0]?.length ?? 0;
  if (length === 0 || sampleRate <= 0) return [];

  const windowSize = Math.max(1, Math.round((sampleRate * WINDOW_MS) / 1000));
  const levelsDb: number[] = [];

  for (let start = 0; start < length; start += windowSize) {
    const end = Math.min(start + windowSize, length);
    let sum = 0;
    for (const channel of channels) {
      for (let i = start; i < end; i++) {
        const sample = channel[i] ?? 0;
        sum += sample * sample;
      }
    }
    const rms = Math.sqrt(sum / ((end - start) * channels.length));
    levelsDb.push(20 * Math.log10(rms + 1e-9));
  }

  const sorted = [...levelsDb].sort((a, b) => a - b);
  const loudDb = sorted[Math.floor(sorted.length * 0.95)] ?? -Infinity;
  const thresholdDb = Math.max(
    ABSOLUTE_FLOOR_DB,
    loudDb - RELATIVE_THRESHOLD_DB,
  );

  const sections: SoundSection[] = [];

  levelsDb.forEach((db, index) => {
    if (db < thresholdDb) return;

    const startMs = index * WINDOW_MS;
    const endMs = startMs + WINDOW_MS;
    const previous = sections.at(-1);

    if (previous && startMs - previous.endMs < MIN_GAP_MS) {
      previous.endMs = endMs;
    } else {
      sections.push({ startMs, endMs });
    }
  });

  return sections.filter(
    (section) => section.endMs - section.startMs >= MIN_SECTION_MS,
  );
}

// 0 = not ducked, 1 = fully ducked.
export function getDuckingAmount(timeMs: number, sections: SoundSection[]) {
  let amount = 0;

  for (const { startMs, endMs } of sections) {
    const attackEnd = startMs - LOOKAHEAD_MS;
    const attackStart = attackEnd - ATTACK_MS;
    const releaseStart = endMs + RELEASE_DELAY_MS;
    const releaseEnd = releaseStart + RELEASE_MS;

    if (timeMs < attackStart || timeMs >= releaseEnd) continue;

    let progress = 1;
    if (timeMs < attackEnd) {
      progress = (timeMs - attackStart) / ATTACK_MS;
    } else if (timeMs > releaseStart) {
      progress = 1 - (timeMs - releaseStart) / RELEASE_MS;
    }

    amount = Math.max(amount, progress * progress * (3 - 2 * progress));
  }

  return amount;
}

export function getDuckedVolume(baseVolume: number, amount: number) {
  if (amount === 0 || baseVolume <= DUCKED_VOLUME) {
    return baseVolume;
  }

  // Interpolate in dB so the fade sounds even at any starting volume.
  return baseVolume * Math.pow(DUCKED_VOLUME / baseVolume, amount);
}
